import axios from "axios";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { storage } from "../../main-component/Firebase";
import { toast } from "react-toastify";
import { Editor } from "@tinymce/tinymce-react";
import { FiArrowLeft, FiFileText, FiImage, FiSave, FiUploadCloud } from "react-icons/fi";
import {
  normalizeBlogTypography,
  getPloomEditorContentStyle,
  getPloomEditorColorMap,
  PLOOM_EDITOR_FONT_FORMATS,
  PLOOM_EDITOR_STYLE_FORMATS,
} from "../../utils/blogTypography";
import "./style.css";

const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
]
  .filter(Boolean)
  .map((email) => email.trim().toLowerCase());

function AdminUpdateBlogForm(props) {
  const { user } = props.user;
  const history = useHistory();
  const { id } = useParams();
  const [blogDetails, setBlogDetails] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [errors, setErrors] = React.useState({});
  const token = process.env.REACT_APP_TOKEN;
  const editorRef = React.useRef(null);

  const isAdmin = ADMIN_EMAILS.includes(
    String(user?.email || "").trim().toLowerCase()
  );

  React.useEffect(() => {
    let active = true;

    const fetchBlogData = async () => {
      try {
        setLoading(true);
        const result = await axios.get(
          `${process.env.REACT_APP_API_BASE}/blog/id/get?id=${id}`,
          { headers: { Authorization: `Basic ${token}` } }
        );
        if (active) {
          setBlogDetails(result.data);
        }
      } catch (error) {
        console.error("Error loading blog:", error);
        toast.error("Articolul nu a putut fi încărcat.");
      } finally {
        if (active) setLoading(false);
      }
    };

    if (isAdmin) fetchBlogData();
    return () => {
      active = false;
    };
  }, [id, isAdmin, token]);

  const changeHandler = ({ target: { name, value } }) => {
    setBlogDetails((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const updateText = (value) => {
    setBlogDetails((current) => ({
      ...current,
      text: value,
      content: value,
    }));
    setErrors((current) => ({ ...current, text: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!blogDetails?.title?.trim()) nextErrors.title = "Titlul este obligatoriu.";
    if (!(blogDetails?.text || blogDetails?.content || "").trim()) {
      nextErrors.text = "Conținutul este obligatoriu.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const uploadCoverIfNeeded = async () => {
    if (!blogDetails?.base64URL) return blogDetails?.coverImage || "";
    const imageRef = ref(storage, `blog/${blogDetails.id || id}`);
    await uploadString(imageRef, blogDetails.base64URL, "data_url");
    return getDownloadURL(imageRef);
  };

  const submitHandler = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const coverImage = await uploadCoverIfNeeded();
      const content = normalizeBlogTypography(blogDetails.text || blogDetails.content || "");
      const payload = {
        ...blogDetails,
        id: blogDetails.id || id,
        coverImage,
        text: content,
        content,
      };
      delete payload.base64URL;

      await axios.post(
        `${process.env.REACT_APP_API_BASE}/blog/post?id=${payload.id}`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      toast.success("Articolul a fost actualizat.");
      history.push(`/blog-single-fullwidth/${payload.id}`);
    } catch (error) {
      console.error("Error updating blog:", error);
      toast.error("Articolul nu a putut fi actualizat.");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selectați un fișier imagine valid.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imaginea poate avea maximum 5 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setBlogDetails((current) => ({ ...current, base64URL: reader.result }));
    };
    reader.onerror = () => toast.error("Imaginea nu a putut fi citită.");
    reader.readAsDataURL(file);
  };

  if (!isAdmin) {
    return <div className="admin-blog-edit__state">Această pagină este disponibilă doar administratorilor.</div>;
  }

  if (loading || !blogDetails) {
    return <div className="admin-blog-edit__state">Se încarcă articolul…</div>;
  }

  return (
    <main className="admin-blog-edit">
      <div className="admin-blog-edit__topbar">
        <button type="button" className="admin-blog-edit__back" onClick={() => history.push("/blog")}>
          <FiArrowLeft />
          Înapoi la blog
        </button>
        <div>
          <span>ADMINISTRARE CONȚINUT</span>
          <h1>Editează articolul</h1>
          <p>Păstrează structura articolului și actualizează doar informațiile necesare.</p>
        </div>
      </div>

      <form onSubmit={submitHandler} className="admin-blog-edit__form">
        <section className="admin-blog-edit__section">
          <header>
            <FiFileText />
            <div>
              <h2>Informații de bază</h2>
              <p>Titlul, autorul și subiectul articolului.</p>
            </div>
          </header>

          <div className="admin-blog-edit__fields">
            <label className="admin-blog-edit__field admin-blog-edit__field--wide">
              <span>Titlu</span>
              <input name="title" value={blogDetails.title || ""} onChange={changeHandler} />
              {errors.title && <small>{errors.title}</small>}
            </label>

            <label className="admin-blog-edit__field">
              <span>Autor</span>
              <input name="author" value={blogDetails.author || ""} onChange={changeHandler} />
            </label>

            <label className="admin-blog-edit__field">
              <span>Subiect</span>
              <input
                name="subject"
                value={blogDetails.subject || blogDetails.category || ""}
                onChange={changeHandler}
              />
            </label>
          </div>
        </section>

        <section className="admin-blog-edit__section">
          <header>
            <FiImage />
            <div>
              <h2>Imagine de copertă</h2>
              <p>Previzualizare și înlocuire rapidă, maximum 5 MB.</p>
            </div>
          </header>

          <div className="admin-blog-edit__cover">
            {(blogDetails.base64URL || blogDetails.coverImage) && (
              <img src={blogDetails.base64URL || blogDetails.coverImage} alt="" />
            )}
            <label className="admin-blog-edit__upload">
              <FiUploadCloud />
              <span>Alege o imagine nouă</span>
              <input type="file" accept="image/png,image/gif,image/jpeg,image/webp" onChange={uploadImage} />
            </label>
          </div>
        </section>

        <section className="admin-blog-edit__section admin-blog-edit__section--editor">
          <header>
            <FiFileText />
            <div>
              <h2>Conținut articol</h2>
              <p>Editorul TinyMCE și toate funcțiile sale sunt păstrate.</p>
            </div>
          </header>

          <div className="admin-blog-edit__editor">
            <Editor
              apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
              onInit={(_, editor) => {
                editorRef.current = editor;
              }}
              value={blogDetails.text || blogDetails.content || ""}
              onEditorChange={updateText}
              init={{
                height: 560,
                menubar: true,
                plugins: [
                  "advlist autolink lists link image charmap preview anchor",
                  "searchreplace visualblocks code fullscreen",
                  "insertdatetime media table code help wordcount",
                ],
                toolbar:
                  "undo redo | styleselect blocks fontselect | bold italic forecolor backcolor | " +
                  "alignleft aligncenter alignright alignjustify | " +
                  "bullist numlist outdent indent | link image | removeformat | preview code fullscreen",
                font_formats: PLOOM_EDITOR_FONT_FORMATS,
                style_formats: PLOOM_EDITOR_STYLE_FORMATS,
                color_map: getPloomEditorColorMap(),
                custom_colors: true,
                content_style: getPloomEditorContentStyle(),
                setup: (editor) => {
                  editor.on("blur", () => {
                    const normalized = normalizeBlogTypography(editor.getContent());
                    if (normalized !== editor.getContent()) editor.setContent(normalized);
                    updateText(normalized);
                  });
                },
              }}
            />
          </div>
          {errors.text && <small className="admin-blog-edit__error">{errors.text}</small>}
        </section>

        <div className="admin-blog-edit__actions">
          <button type="button" className="admin-blog-edit__cancel" onClick={() => history.push("/blog")}>
            Anulează
          </button>
          <button type="submit" className="admin-blog-edit__save" disabled={submitting}>
            <FiSave />
            {submitting ? "Se salvează…" : "Salvează articolul"}
          </button>
        </div>
      </form>
    </main>
  );
}

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(AdminUpdateBlogForm);
